/**
 * Case state v2 — the portal's copy of the customer-facing labels
 * (migration 084).
 *
 * ⚠️ Mirrored rather than imported from @mediloop/shared on purpose:
 * that package's `main` is index.ts and nothing in apps/* imports it
 * at runtime, so a value import would be the first — and it would only
 * fail at build time on the host, not here. TYPES from the shared
 * package are erased at compile time and stay safe to import, which is
 * exactly what the `satisfies` clauses below lean on: if a stage or a
 * waiting-on value is ever added to the union in
 * packages/shared/types.ts, this file stops compiling until it's
 * handled here too. See the note in packages/shared/constants.ts.
 */

import type { CaseOutcome, CaseStage, CaseWaitingOn, ResolutionPath } from '../types/bill';

/** The five stages the customer progress bar renders, in order.
 *  Blockers are deliberately absent: "waiting on you" is the
 *  needed-from-you card, never a stage. */
export const CASE_STAGES = [
  'received',
  'reviewing',
  'working_resolution',
  'verifying_outcome',
  'resolved',
] as const satisfies readonly CaseStage[];

export const CASE_STAGE_LABELS = {
  received: 'Case received',
  reviewing: 'Reviewing your bill',
  working_resolution: 'Working on your resolution',
  verifying_outcome: 'Confirming the outcome',
  resolved: 'Resolved',
} satisfies Record<CaseStage, string>;

/** Customer-facing "Waiting on:" wording. `mediloop` reads as "Our
 *  team" (the lowercase brand word doesn't belong in a label slot) and
 *  `customer` addresses them directly. */
export const CASE_WAITING_ON_LABELS = {
  mediloop: 'Our team',
  customer: 'You',
  provider: 'Provider',
  insurer: 'Insurer',
  collections_agency: 'Collections agency',
  none: 'Nothing right now',
} satisfies Record<CaseWaitingOn, string>;

/** What we pursued, in the customer's words. Shown once a path is set
 *  so "Working on your resolution" isn't a black box. */
export const RESOLUTION_PATH_LABELS = {
  billing_audit: 'Auditing the itemized charges',
  billing_correction: 'Getting the billing errors corrected',
  insurance_reprocessing: 'Getting your insurer to reprocess the claim',
  provider_negotiation: 'Negotiating with the provider',
  /* Not "applying for" — nobody has applied yet at the point this first
     shows, and telling someone we're seeking charity on their behalf
     before we've asked about their household reads badly. */
  financial_assistance: 'Checking what you qualify for',
  payment_arrangement: 'Arranging a payment plan',
  collections_resolution: 'Resolving it with the collections agency',
} satisfies Record<ResolutionPath, string>;

/** Short headline for the final result. */
export const CASE_OUTCOME_LABELS = {
  savings_secured: 'Savings secured',
  billing_corrected: 'Billing corrected',
  insurance_reprocessed: 'Insurance reprocessed',
  financial_assistance_approved: 'Financial assistance approved',
  payment_arrangement: 'Payment arrangement in place',
  no_reduction: 'No reduction possible',
  customer_withdrew: 'Case closed at your request',
  unable_to_proceed: 'We couldn’t take this further',
  customer_unresponsive: 'Closed, we didn’t hear back',
} satisfies Record<CaseOutcome, string>;

/** The customer-safe explanation of what that outcome means for them.
 *  Every case ends with a sentence a person can act on — including the
 *  ones that didn't go our way, which is where a vague status is worst. */
export const CASE_OUTCOME_EXPLANATIONS = {
  savings_secured:
    'We got the amount you owe reduced. The figures below are what changed.',
  billing_corrected:
    'We found errors on the bill and got them corrected, so you’re only being charged for what actually happened.',
  insurance_reprocessed:
    'We got your insurer to reprocess the claim, so more of this bill is covered than before.',
  financial_assistance_approved:
    'Your application for financial assistance was approved, which reduces or clears what you owe.',
  payment_arrangement:
    'We arranged a payment plan you can manage, which also keeps the bill out of collections.',
  no_reduction:
    'We went through this bill line by line and pushed on every angle available, but the provider held firm. Nothing here is wrong or overcharged as far as we can prove.',
  customer_withdrew:
    'You asked us to stop work on this one, so we closed it. Nothing further is happening on your side.',
  unable_to_proceed:
    'We weren’t able to take this one further. Message us if anything has changed and we’ll take another look.',
  customer_unresponsive:
    'We needed something from you to keep going and didn’t hear back, so we closed this for now. Message us whenever you’re ready and we’ll pick it back up.',
} satisfies Record<CaseOutcome, string>;

/** Outcomes where a savings-style verdict panel makes sense. The rest
 *  get the plain outcome card — showing a "what we saved you" frame on
 *  a withdrawn case reads as tone-deaf. */
export const VERDICT_OUTCOMES: readonly CaseOutcome[] = [
  'savings_secured',
  'billing_corrected',
  'insurance_reprocessed',
  'financial_assistance_approved',
  'payment_arrangement',
  'no_reduction',
];
